import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
  Paper,
  alpha,
  IconButton,
  Tooltip,
  Stack,
  useTheme
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Edit as EditIcon,
  Link as LinkIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  MusicNote as MusicNoteIcon,
  Dashboard as DashboardIcon
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { siteCmsUrls } from "../../api/siteCms";

const SocialMediaView = () => {
  const theme = useTheme();
  const baseColors = tokens(theme.palette.mode);

  // Using the same color scheme as in the Dashboard component
  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  const [socialData, setSocialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(siteCmsUrls.socialLinks);
      if (response.data.status) {
        setSocialData(response.data.data);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.message);
      setSnackbarMessage("Error fetching social media data");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEditClick = () => {
    navigate(`/social/editLinks`);
  };

  // Social media platform configurations
  const socialLinks = [
    {
      name: "Facebook",
      link: socialData?.fbLink || "",
      icon: <FacebookIcon />,
      color: "#1877F2"
    },
    {
      name: "Instagram",
      link: socialData?.instagramLink || "",
      icon: <InstagramIcon />,
      color: "#E4405F"
    },
    {
      name: "TikTok",
      link: socialData?.tikTokLink || "",
      icon: <MusicNoteIcon />,
      color: "#000000"
    },
    {
      name: "YouTube",
      link: socialData?.youtubeLink || "",
      icon: <YouTubeIcon />,
      color: "#FF0000"
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section - Styled similarly to the Dashboard header */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <DashboardIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Social Media Links
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Manage your social media presence and connections
        </Typography>
      </Box>

      {/* Social Links Grid */}
      <Grid container spacing={3}>
        {socialLinks.map((platform, index) => (
          <Grid item xs={12} key={index}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  backgroundColor: "#ffffff",
                },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    backgroundColor: alpha(colors.blueAccent[500], 0.2),
                    borderRadius: "8px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {React.cloneElement(platform.icon, {
                    sx: {
                      color: colors.blueAccent[500],
                      fontSize: 24,
                    },
                  })}
                </Box>
                <Box flex={1}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="#333333"
                    mb={0.5}
                  >
                    {platform.name}
                  </Typography>
                  <Typography variant="body2" color="#666666" sx={{ wordBreak: "break-all" }}>
                    {platform.link || "Not set"}
                  </Typography>
                </Box>
                {platform.link && (
                  <Tooltip title={`Visit ${platform.name}`}>
                    <IconButton
                      href={platform.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        backgroundColor: alpha(colors.blueAccent[500], 0.1),
                        '&:hover': {
                          backgroundColor: alpha(colors.blueAccent[500], 0.2)
                        }
                      }}
                    >
                      <LinkIcon sx={{ color: colors.blueAccent[500] }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Edit Button */}
      <Box mt={3} display="flex" justifyContent="center">
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          sx={{
            backgroundColor: colors.blueAccent[500],
            color: "white",
            fontSize: "14px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
          }}
        >
          Edit Social Media Links
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default SocialMediaView;